/**
 * UTILITY: Fix Admin Role
 * Use this to manually ensure the current logged-in user has admin role in Firestore
 * Run from browser console: window.fixAdminRole()
 */

import { doc, getDoc, setDoc } from '../services/firestoreCompat';
import { auth, db } from '../services/firebaseConfig';

export async function fixAdminRole(): Promise<{ success: boolean; message: string }> {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        message: 'No user is currently logged in. Please log in first.',
      };
    }

    console.log('🔧 [FIX ADMIN ROLE] Fixing admin role for user:', user.email);
    console.log('🔧 [FIX ADMIN ROLE] User UID:', user.uid);

    const userDocRef = doc(db, 'users', user.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      // Create user document if it doesn't exist
      console.log('🔧 [FIX ADMIN ROLE] Creating user document in Firestore...');
      await setDoc(userDocRef, {
        uid: user.uid,
        email: user.email || '',
        name: user.displayName || user.email?.split('@')[0] || 'Admin',
        first_name: user.displayName?.split(' ')[0] || user.email?.split('@')[0] || 'Admin',
        last_name: user.displayName?.split(' ').slice(1).join(' ') || '',
        role: 'admin',
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_login: new Date().toISOString(),
        permissions: ['read_orders', 'write_orders', 'manage_users', 'manage_settings', 'manage_customers'],
      });
      console.log('✅ [FIX ADMIN ROLE] User document created with admin role');
      return {
        success: true,
        message: 'User document created with admin role successfully!',
      };
    } else {
      // Update existing document to ensure role is 'admin'
      const userData = userDocSnap.data();
      if (!userData.role || userData.role !== 'admin') {
        console.log('🔧 [FIX ADMIN ROLE] Updating user document with admin role...');
        await setDoc(userDocRef, {
          ...userData,
          role: 'admin',
          updated_at: new Date().toISOString(),
          last_login: new Date().toISOString(),
          permissions: ['read_orders', 'write_orders', 'manage_users', 'manage_settings', 'manage_customers'],
        }, { merge: true });
        console.log('✅ [FIX ADMIN ROLE] User document updated with admin role');
        return {
          success: true,
          message: 'User document updated with admin role successfully!',
        };
      } else {
        console.log('✅ [FIX ADMIN ROLE] User document already has admin role');
        return {
          success: true,
          message: 'User already has admin role. No changes needed.',
        };
      }
    }
  } catch (error: any) {
    console.error('❌ [FIX ADMIN ROLE] Error:', error);
    return {
      success: false,
      message: `Error fixing admin role: ${error.message || 'Unknown error'}`,
    };
  }
}

// Make it available globally for browser console access
if (typeof window !== 'undefined') {
  (window as any).fixAdminRole = fixAdminRole;
}

